// Panel administrativo interno de UCORP (docs/PLAN-ADMIN-UCORP.md y
// docs/PLAN-BACKOFFICE-V2.md). Shapes del scope /admin/*: vista cross-tenant
// para los fundadores.
// Invariante del plan: sin dinero de providers (el unico dinero es el de
// AWS, costos propios de UCORP) y PII agregada (conteos, nunca el padron).
// Las integraciones AWS (costos, budgets, pool de usuarios) degradan con
// { available: false, reason } y status 200: un permiso IAM faltante no es
// un error del panel.
import { z } from "zod";
import { Coordinates, Decimal, Integer, CalendarDate, LocalTime, Instant, SiteId, ProviderId, RecordId, VehicleType, } from "./common.js";
import { MobileApplication } from "./mobile-auth.js";
import { UpdateSiteParametersRequest } from "./site-parameters.js";
import { TrackedVehiclePosition, TripResponse } from "./trips.js";
// --- Comunes del panel -----------------------------------------------------
export const UserRole = z
    .enum([
    "site_admin",
    "site_operator",
    "provider_operator",
    "passenger",
    "ucorp_admin",
    "ucorp_admin_reader",
])
    .openapi("UserRole", {
    description: "Rol de authorizacion en la plataforma. ucorp_admin es el rol interno de UCORP que edita desde la consola; ucorp_admin_reader es el rol interno de solo lectura (ve todo el backoffice, no escribe; la API valida server-side). El resto son los roles por tenant del manifiesto.",
});
export const IntegrationUnavailable = z
    .object({
    available: z.literal(false),
    reason: z.string().min(1).openapi({
        description: "Por que no hay datos: permiso IAM faltante, SDK ausente, error del servicio.",
        example: "AccessDeniedException al invocar GetCostAndUsage",
    }),
})
    .openapi("IntegrationUnavailable", {
    description: "Degradacion controlada de una integracion AWS: la respuesta es 200 con available=false, nunca un 500.",
});
// --- GET /admin/tenants ----------------------------------------------------
export const TripCountByStatus = z
    .object({
    scheduled: Integer.min(0),
    in_progress: Integer.min(0),
    completed: Integer.min(0),
    cancelled: Integer.min(0),
})
    .openapi("TripCountByStatus", {
    description: "Viajes de hoy del tenant, contados por estado.",
});
export const AdminTenantSummary = z
    .object({
    siteId: SiteId,
    name: z.string().min(1).openapi({
        description: "Nombre comercial de la planta.",
        example: "Planta Bahia Blanca",
    }),
    providers: Integer.min(0),
    vehicles: Integer.min(0),
    records: Integer.min(0).openapi({
        description: "Cantidad de personas en el padron. Solo el conteo: la PII se muestra agregada en v1.",
    }),
    users: Integer.min(0),
    tripsToday: TripCountByStatus,
})
    .openapi("AdminTenantSummary", {
    description: "Resumen de un tenant para la lista del panel admin. Excluye al tenant interno 'ucorp'.",
});
export const AdminListTenantsResponse = z.array(AdminTenantSummary).openapi("AdminListTenantsResponse", {
    description: "Lista de tenants cliente con sus agregados del dia.",
});
// --- GET /admin/tenants/:siteId ------------------------------------------
export const SiteStatus = z
    .enum(["prospect", "pilot", "active", "suspended", "offboarded"])
    .openapi("SiteStatus", {
    description: "Ciclo de vida comercial del tenant: prospecto (en conversaciones), piloto, activa (operando), suspendida (danger zona) o baja.",
});
export const SiteType = z.enum(["customer", "internal"]).openapi("SiteType", {
    description: "Tipo de tenant: cliente (planta industrial real) o interno (el compartimento propio de UCORP, excluido de listados de clientes).",
});
export const InstitutionalSite = z
    .object({
    id: SiteId,
    tradeName: z.string().min(1),
    legalName: z.string().min(1).optional(),
    timezone: z.string().min(1).openapi({
        description: "Zona horaria IANA de la planta: toda fecha operativa se interpreta en ella.",
        example: "America/Argentina/Buenos_Aires",
    }),
    mailDomains: z.array(z.string().min(1)),
    status: SiteStatus,
    type: SiteType,
    cuit: z.string().min(1).optional().openapi({
        description: "CUIT de la razon social. Solo la identidad fiscal, jamas montos.",
        example: "30-12345678-9",
    }),
    corporateGroup: z.string().min(1).optional().openapi({
        description: "Grupo economico al que pertenece la planta (ej: Dow, Profertil).",
    }),
    firstContactAt: CalendarDate.optional().openapi({
        description: "Fecha del primer contacto comercial con la planta.",
    }),
    pilotStartAt: CalendarDate.optional().openapi({
        description: "Fecha de inicio del piloto, si lo hubo.",
    }),
    goLiveAt: CalendarDate.optional().openapi({
        description: "Fecha de go-live en produccion.",
    }),
    internalNotes: z.string().optional().openapi({
        description: "Notas libres de los fundadores sobre la relacion con el cliente. Nunca salen del backoffice.",
    }),
})
    .openapi("InstitutionalSite", {
    description: "Identidad y ciclo de vida comercial de un tenant: lo que responde el PATCH institucional y el cambio de estado.",
});
export const LocationGate = z
    .object({
    id: z.string().min(1),
    name: z.string().min(1),
    coordinates: Coordinates,
})
    .openapi("LocationGate", {
    description: "Acceso de una sede donde suben y bajan los pasajeros.",
});
export const TenantLocation = z
    .object({
    id: z.string().min(1),
    name: z.string().min(1),
    coordinates: Coordinates,
    gates: z.array(LocationGate),
})
    .openapi("TenantLocation", { description: "Sede fisica del tenant con sus porterias." });
export const TenantShift = z
    .object({
    id: z.string().min(1),
    name: z.string().min(1),
    startTime: LocalTime,
    endTime: LocalTime.openapi({
        description: "Hora de salida en HH:MM. Si es menor que la de ingreso, el turno cruza la medianoche.",
    }),
    // PENDIENTE (bloqueado fuera de contracts): enum DiaDeSemana. La columna
    // shifts.days es jsonb string[] y data/admin-repository.ts la copia sin
    // mapear; el catalogo (monday..sunday) queda en la description.
    days: z.array(z.string().min(1)).openapi({
        description: "Dias de la semana en que rige el turno: monday, tuesday, wednesday, thursday, friday, saturday, sunday.",
    }),
})
    .openapi("TenantShift", { description: "Un turno de la planta, en hora local." });
export const OperatingArea = z
    .object({
    latMin: Decimal.min(-90).max(90),
    latMax: Decimal.min(-90).max(90),
    lngMin: Decimal.min(-180).max(180),
    lngMax: Decimal.min(-180).max(180),
})
    .openapi("OperatingArea", {
    description: "Rectangulo del area de operacion: toda coordenada fuera es casi seguro un error de carga.",
});
export const TenantParameters = z
    .object({
    maxJourneyMin: Integer.positive().openapi({
        description: "Maximo de minutos que un pasajero puede pasar arriba del vehiculo. Regla dura del contrato de transporte.",
    }),
    requestLeadTimeHours: Integer.positive().openapi({
        description: "Antelacion de la combi, en horas, contada desde la busqueda del pasajero (AT-12; antes era una sola por planta).",
    }),
    cabRequestLeadTimeHours: Integer.min(0).optional().openapi({
        description: "Antelacion del auto, en horas, contada desde la busqueda del pasajero; 0 es sin antelacion. Opcional por compatibilidad (AT-12).",
    }),
    pickerStepMinutes: Integer.positive(),
    area: OperatingArea,
})
    .openapi("TenantParameters", { description: "Parametros operativos del tenant." });
export const CostCenter = z
    .object({ id: z.string().min(1), name: z.string().min(1) })
    .openapi("CostCenter", {
    description: "Centro de costos para imputacion de viajes. Sin montos, solo la imputacion.",
});
export const FleetVehicle = z
    .object({
    id: z.string().min(1),
    type: VehicleType,
    capacity: Integer.positive(),
    plate: z.string().min(1),
})
    .openapi("FleetVehicle", {
    description: "Un vehiculo de la flota del proveedor. Sin tarifas: la plataforma no maneja dinero de proveedores.",
});
export const TenantProvider = z
    .object({
    id: ProviderId,
    brandName: z.string().min(1),
    serviceTypes: z.array(VehicleType),
    operatingZone: z.string().min(1),
    active: z.boolean().optional().openapi({
        description: "Habilitacion del proveedor en la planta (providers.active, AT-5): false cuando la planta o UCORP lo deshabilito y su nomina deja de ser elegible aca. Ausente: API anterior a 0.6.30 o detalle armado desde el manifiesto sin base; la consola lo muestra como desconocido y no ofrece el cambio.",
    }),
    fleet: z.array(FleetVehicle).openapi({
        description: "Detalle de la flota del proveedor, vehiculo por vehiculo.",
    }),
})
    .openapi("TenantProvider", { description: "Empresa transportista del tenant con su flota." });
export const LineStop = z
    .object({
    id: z.string().min(1),
    name: z.string().min(1),
    order: Integer.min(0),
    offsetMinutes: Integer.min(0),
    coordinates: Coordinates,
})
    .openapi("LineStop", {
    description: "Parada de una linea de colectivo, con su desfase respecto del inicio.",
});
export const BusLine = z
    .object({
    id: z.string().min(1),
    name: z.string().min(1),
    providerId: ProviderId,
    firstPass: LocalTime,
    lastPass: LocalTime,
    intervalMinutes: Integer.positive(),
    stops: z.array(LineStop).openapi({
        description: "Paradas del recorrido en orden, con su desfase respecto del inicio.",
    }),
})
    .openapi("BusLine", { description: "Linea de colectivo de recorrido fijo." });
export const TenantUser = z
    .object({ email: z.string().email(), role: UserRole })
    .openapi("TenantUser", { description: "Usuario del tenant con su rol." });
export const AdminTenantDetail = z
    .object({
    site: InstitutionalSite,
    locations: z.array(TenantLocation).openapi({
        description: "Sedes fisicas del tenant con sus porterias (los accesos donde suben y bajan los pasajeros).",
    }),
    shifts: z.array(TenantShift).openapi({
        description: "Esquema de turnos de la planta, en hora local.",
    }),
    holidays: z.array(CalendarDate).openapi({
        description: "Fechas no laborables declaradas en el manifiesto.",
    }),
    parameters: TenantParameters.optional().openapi({
        description: "Parametros operativos del tenant. Opcional: un tenant onboardeado antes de la migracion 0003 puede no tenerlos persistidos todavia.",
    }),
    costCenters: z.array(CostCenter),
    providers: z.array(TenantProvider),
    lines: z.array(BusLine),
    // PENDIENTE (bloqueado fuera de contracts): array de pares
    // { policy, records }. data/admin-repository.ts construye el Record y
    // tipa su ficha con este schema; cambia cuando el repositorio mapee.
    recordsByPolicy: z.record(z.string(), Integer.min(0)).openapi({
        description: "Conteo de legajos por tipo de politica de movilidad efectiva (shuttle, cab, bus, combined); un legajo sin politica valida cuenta con la de la planta (shuttle). PII agregada: NO se listan personas, domicilios ni telefonos en la ficha.",
        example: { shuttle: 18, bus: 9 },
    }),
    recordsWithoutPolicy: Integer.min(0).optional().openapi({
        description: "Legajos cuya politica de movilidad guardada no es valida: toman la de la planta (su modalidad por defecto, defaultMobilityMode) y ya estan contados con ella en recordsByPolicy. Todo empleado tiene politica (Federico, 2026-09-25); un numero mayor a 0 es un dato a corregir en el manifiesto. Ausente: no informado.",
    }),
    users: z.array(TenantUser),
})
    .openapi("AdminTenantDetail", {
    description: "Ficha completa de un tenant (consola v2): identidad y ciclo de vida comercial, estructura fisica (sedes y porterias), calendario, parametros, proveedores con flota en detalle y lineas con paradas. El padron sigue agregado (conteos por politica): la PII vive solo en vistas explicitas de gestion.",
});
// --- GET /admin/tenants/:siteId/operation ---------------------------------
export const AdminOperationResponse = z
    .object({
    trips: z.array(TripResponse),
    positions: z.array(TrackedVehiclePosition),
})
    .openapi("AdminOperationResponse", {
    description: "Operacion del dia de un tenant, vista desde el panel admin: los mismos shapes que consume la torre (viajes de hoy + ultimas posiciones).",
});
// --- GET /admin/health ------------------------------------------------------
export const TickVerdict = z.enum(["fresh", "stale", "no_activity"]).openapi("TickVerdict", {
    description: "Estado del generador de posiciones de un tenant. no_activity NO es un error: fuera de la ventana operativa no hay viajes en curso y por lo tanto no hay posiciones.",
});
export const SiteTick = z
    .object({
    siteId: SiteId,
    lastPosition: Instant.nullable().openapi({
        description: "Instant de la ultima posicion registrada del tenant. null significa que NUNCA hubo una posicion (distinto de 'sin dato').",
    }),
    positionsLast5Min: Integer.min(0),
    tripsInProgress: Integer.min(0),
    verdict: TickVerdict,
})
    .openapi("SiteTick", { description: "Frescura del tick de un tenant." });
export const BaseStatus = z.enum(["ok", "error"]).openapi("BaseStatus", {
    description: "Resultado de un select 1 contra la base (ok en modo memoria: no hay base que probar).",
});
export const AdminHealthResponse = z
    .object({
    api: z.enum(["ok"]),
    base: BaseStatus,
    ticks: z.array(SiteTick),
})
    .openapi("AdminHealthResponse", {
    description: "Salud tecnica de la plataforma para la franja superior del panel admin.",
});
// --- GET /admin/costs -----------------------------------------------------
export const BilledService = z
    .object({
    service: z.string().min(1).openapi({ example: "Amazon Relational Database Service" }),
    amountUsd: Decimal.min(0),
})
    .openapi("BilledService", { description: "Gasto AWS del mes de un servicio." });
export const CostsQuery = z
    .object({
    month: z
        .string()
        .regex(/^\d{4}-\d{2}$/)
        .optional()
        .openapi({
        description: "Mes calendario a consultar (YYYY-MM). Por defecto el mes en curso; no futuro y hasta 12 meses atras.",
        example: "2026-09",
    }),
})
    .openapi("CostsQuery", { description: "Filtros de GET /admin/costs." });
export const AvailableCosts = z
    .object({
    available: z.literal(true),
    month: z.string().regex(/^\d{4}-\d{2}$/).openapi({
        description: "Mes calendario consultado (YYYY-MM).",
        example: "2026-09",
    }),
    currency: z.enum(["USD"]),
    totalUsd: Decimal.min(0).openapi({
        description: "USO real del mes (sin netear creditos ni refunds): lo que costo operar, se facture o no.",
    }),
    creditsUsd: Decimal.min(0).optional().openapi({
        description: "Creditos AWS aplicados en el mes (valor absoluto). El neto facturado es totalUsd - creditsUsd (piso 0).",
    }),
    services: z.array(BilledService),
})
    .openapi("AvailableCosts", { description: "Costos AWS del mes, por servicio." });
// z.union y no discriminatedUnion: el discriminador es booleano y OpenAPI
// solo admite discriminadores string; el wire queda como oneOf de dos schemas
// con nombre (el generador Kotlin elige por la forma). El parse es el mismo.
export const AdminCostsResponse = z
    .union([AvailableCosts, IntegrationUnavailable])
    .openapi("AdminCostsResponse", {
    description: "Gasto AWS del mes calendario por servicio (Cost Explorer, cacheado 6 horas: cada solicitud a CE cuesta USD 0,01). Los montos son USO real: los creditos promocionales se informan aparte para que un mes cubierto por creditos no aparezca en cero. El unico dinero del panel: costos propios de UCORP, jamas dinero de proveedores.",
});
// --- GET /admin/budgets ----------------------------------------------------
export const BudgetStatusLight = z.enum(["on_track", "running_out", "imminent"]).openapi("BudgetStatusLight", {
    description: "Semaforo del budget (patron de la demo): on_track (dentro de lo previsto), running_out (el forecast supera el limite), imminent (mas del 90% ya usado).",
});
export const BudgetAws = z
    .object({
    name: z.string().min(1),
    limitUsd: Decimal.min(0),
    usedUsd: Decimal.min(0),
    forecastUsd: Decimal.min(0).optional(),
    statusLight: BudgetStatusLight,
})
    .openapi("BudgetAws", { description: "Un budget de la cuenta AWS con su semaforo." });
export const AvailableBudgets = z
    .object({
    available: z.literal(true),
    budgets: z.array(BudgetAws),
})
    .openapi("AvailableBudgets", { description: "Budgets AWS de la cuenta." });
export const AdminBudgetsResponse = z
    .union([AvailableBudgets, IntegrationUnavailable])
    .openapi("AdminBudgetsResponse", { description: "Budgets AWS de la cuenta, mapeados a semaforo." });
// --- GET /admin/user-pool ----------------------------------------------
export const PoolUser = z
    .object({
    email: z.string().min(1),
    status: z.string().min(1).openapi({
        description: "UserStatus de Cognito (CONFIRMED, FORCE_CHANGE_PASSWORD, ...).",
        example: "CONFIRMED",
    }),
    mfa: z.boolean().optional().openapi({
        description: "Si el usuario tiene MFA configurado, cuando el pool lo informa.",
    }),
    createdAt: Instant.optional(),
    siteId: SiteId.optional().openapi({
        description: "Valor del atributo custom:planta_id, si esta seteado.",
    }),
})
    .openapi("PoolUser", { description: "Usuario del usuario pool de Cognito." });
export const AvailablePoolUsers = z
    .object({
    available: z.literal(true),
    users: z.array(PoolUser),
})
    .openapi("AvailablePoolUsers", { description: "Usuarios del pool de Cognito." });
export const AdminUserPoolResponse = z
    .union([AvailablePoolUsers, IntegrationUnavailable])
    .openapi("AdminUserPoolResponse", {
    description: "Usuarios del usuario pool de Cognito (ListUsers): estado de la cuenta, MFA y planta asignada. Cognito autentica; los roles viven en la base (ADR-006).",
});
// --- Consola v2: entidades vivas del backoffice ------------------------------
// Frontera de gobernanza: el manifiesto Git define identidad y estructura; el
// backoffice edita el status del mundo real (contacts, agreements, incidents,
// overrides). Ningun request de escritura admite fields de dinero.
export const TenantResourceParams = z
    .object({
    siteId: SiteId,
    id: z.string().min(1).openapi({ description: "Identificador del recurso anidado." }),
})
    .openapi("TenantResourceParams", {
    description: "Parametros de path de /admin/tenants/:siteId/<coleccion>/:id.",
});
// --- Contactos ---------------------------------------------------------------
export const ContactRole = z
    .enum([
    "commercial_sponsor",
    "operations",
    "it_security",
    "hr",
    "hse",
    "billing",
    "incidents_24_7",
])
    .openapi("ContactRole", {
    description: "Rol del contacto en la matriz de escalamiento del cliente: sponsor comercial, operaciones, IT/seguridad, RRHH, HSE, facturacion o guardia de incidentes 24/7.",
});
export const AdminContact = z
    .object({
    id: z.string().min(1),
    name: z.string().min(1),
    role: ContactRole,
    email: z.string().email().optional(),
    phone: z.string().min(1).optional(),
    onCallPhone: z.string().min(1).optional().openapi({
        description: "Celular de guardia para incidentes fuera de horario (rol incidents_24_7).",
    }),
    escalationOrder: Integer.positive().optional().openapi({
        description: "Posicion en la cadena de escalamiento (1 = primer contacto a llamar).",
    }),
    effectiveFrom: CalendarDate.optional(),
    effectiveTo: CalendarDate.optional(),
    deactivatedAt: Instant.optional().openapi({
        description: "Instant de la baja logica; ausente si el contacto esta vigente.",
    }),
    deactivatedBy: z.string().min(1).optional().openapi({
        description: "Admin que dio de baja el contacto.",
    }),
})
    .openapi("AdminContact", {
    description: "Un contacto del cliente. El dato mas volatil del tenant: vive en la base, jamas en Git. Las bajas son logicas (soft delete).",
});
export const AdminCreateContactRequest = z
    .object({
    name: z.string().min(1),
    role: ContactRole,
    email: z.string().email().optional(),
    phone: z.string().min(1).optional(),
    onCallPhone: z.string().min(1).optional(),
    escalationOrder: Integer.positive().optional(),
    effectiveFrom: CalendarDate.optional(),
    effectiveTo: CalendarDate.optional(),
})
    .openapi("AdminCreateContactRequest", {
    description: "Alta de un contacto del cliente desde la consola. Queda auditada.",
});
export const AdminUpdateContactRequest = AdminCreateContactRequest.partial().openapi("AdminUpdateContactRequest", {
    description: "Edicion parcial de un contacto: solo los campos presentes se modifican. Queda auditada con before y after.",
});
export const AdminDeactivateContactRequest = z
    .object({
    reason: z.string().min(1).openapi({
        description: "Por que se da de baja el contacto (ej: dejo la empresa).",
    }),
})
    .openapi("AdminDeactivateContactRequest", {
    description: "Baja logica de un contacto: setea deactivated_at y el author; nada se borra fisicamente.",
});
// --- Acuerdos ------------------------------------------------------------------
export const AdminAgreements = z
    .object({
    offboardingDataRetentionDays: Integer.positive().optional().openapi({
        description: "Dias que se retienen los datos de un legajo tras su baja (clasificacion-y-datos.md seccion 6).",
    }),
    logRetentionDays: Integer.positive().optional(),
    incidentNotificationDeadlineHours: Integer.positive().openapi({
        description: "Plazo comprometido para notificar incidentes al cliente. Default contractual: 72 horas (respuesta-a-incidentes.md seccion 5).",
    }),
    incidentReportChannel: z.string().min(1).optional().openapi({
        description: "Por donde se reporta un incidente a este cliente (mail, telefono, portal).",
    }),
    ndaSignedAt: CalendarDate.optional(),
    dpaSignedAt: CalendarDate.optional(),
    effectiveTo: CalendarDate.optional(),
})
    .openapi("AdminAgreements", {
    description: "Parametros contractuales del tenant SIN dinero: retenciones, plazos de notificacion y fechas de firma de NDA y DPA. Los montos no existen en la plataforma por decision de gobernanza.",
});
export const AdminUpdateAgreementsRequest = AdminAgreements.partial().openapi("AdminUpdateAgreementsRequest", {
    description: "Edicion parcial de los acuerdos del tenant: solo los campos presentes se modifican. Queda auditada.",
});
// --- Incidentes ------------------------------------------------------------------
export const IncidentSeverity = z.enum(["sev1", "sev2", "sev3"]).openapi("IncidentSeverity", {
    description: "Severidad del incidente: sev1 (critico), sev2 (mayor), sev3 (menor).",
});
export const IncidentStatus = z.enum(["open", "contained", "resolved"]).openapi("IncidentStatus", {
    description: "Estado del incidente en su ciclo de gestion: abierto, contenido o resuelto.",
});
export const IncidentMilestone = z
    .object({
    instant: Instant,
    description: z.string().min(1),
})
    .openapi("IncidentMilestone", {
    description: "Un hito de la cronologia del incidente: que paso y cuando.",
});
export const AdminIncident = z
    .object({
    id: z.string().min(1),
    siteId: SiteId,
    severity: IncidentSeverity,
    title: z.string().min(1),
    timeline: z.array(IncidentMilestone).openapi({
        description: "Cronologia completa del incidente, en orden.",
    }),
    status: IncidentStatus,
    rootCause: z.string().min(1).optional().openapi({
        description: "Causa raiz identificada; se completa al cerrar el analisis.",
    }),
    detectedAt: Instant,
    resolvedAt: Instant.optional(),
})
    .openapi("AdminIncident", {
    description: "Un incidente del servicio para un tenant: el registro que respuesta-a-incidentes.md seccion 8 exige.",
});
export const AdminCreateIncidentRequest = z
    .object({
    severity: IncidentSeverity,
    title: z.string().min(1),
    detectedAt: Instant,
    timeline: z.array(IncidentMilestone).optional().openapi({
        description: "Hitos iniciales, si ya se conocen al momento del alta.",
    }),
})
    .openapi("AdminCreateIncidentRequest", {
    description: "Alta de un incidente desde la consola. Queda auditada.",
});
export const AdminUpdateIncidentRequest = z
    .object({
    severity: IncidentSeverity.optional(),
    title: z.string().min(1).optional(),
    status: IncidentStatus.optional(),
    rootCause: z.string().min(1).optional(),
    resolvedAt: Instant.optional(),
    addMilestone: IncidentMilestone.optional().openapi({
        description: "Hito a APPENDEAR a la cronologia. La cronologia nunca se reescribe entera: solo crece.",
    }),
})
    .openapi("AdminUpdateIncidentRequest", {
    description: "Edicion parcial de un incidente: cambio de estado, causa raiz, cierre o nuevo hito de cronologia. Queda auditada.",
});
// --- Overrides de policy ----------------------------------------------------
// Excepciones temporales (AT-23, docs/PLAN-AT-23-EXCEPCIONES.md): rigen en el
// armado, el despacho, la asignacion y el cierre para las fechas operativas
// de su rango. Los rangos de valor son los del PATCH de parametros (mismo
// schema por campo): una excepcion nunca admite lo que la seccion no admite.
export const OverridableParameter = z
    .enum([
    "maxJourneyMin",
    "shuttleRequestLeadTimeHours",
    "cabRequestLeadTimeHours",
    "arrivalBufferMin",
    "handoverMarginMin",
    "cabAcceptDeadlineSeconds",
    "cabAssignDeadlineSeconds",
    "shuttleAcceptDeadlineSeconds",
    "shuttleAssignDeadlineSeconds",
])
    .openapi("OverridableParameter", {
    description: "Parametro de planta que admite excepcion temporal (OVERRIDABLE_PARAMETERS de @ucorp/domain). Mismo nombre que en el PATCH de parametros.",
});
export const AdminPolicyOverride = z
    .object({
    id: z.string().min(1),
    field: z.string().min(1).openapi({
        description: "Parametro que se pisa (OverridableParameter). Texto y no enum: una fila anterior a 0.7.4 puede nombrar uno que no admite excepcion; esa no rige (applies false).",
    }),
    value: z.string().min(1).openapi({
        description: "Valor temporal que reemplaza al de la planta, serializado como texto (entero).",
    }),
    reason: z.string().min(1),
    author: z.string().min(1),
    effectiveFrom: CalendarDate.openapi({
        description: "Desde 0.7.4. Primera fecha operativa en que rige, en la zona de la planta (inclusiva).",
    }),
    effectiveTo: CalendarDate.openapi({
        description: "Desde 0.7.4. Ultima fecha operativa en que rige, en la zona de la planta (inclusiva).",
    }),
    applies: z.boolean().openapi({
        description: "Desde 0.7.4. true si el parametro admite excepcion y el valor cae en su rango: rige en su rango de fechas. false: queda registrada y no cambia nada.",
    }),
    expiresAt: Instant.openapi({
        description: "Instante en que deja de regir sola: el inicio del dia siguiente a effectiveTo en la zona de la planta.",
    }),
    deactivatedAt: Instant.optional().openapi({
        description: "Baja anticipada del override, si se levanto antes de su vencimiento. Con baja no rige en ninguna fecha.",
    }),
})
    .openapi("AdminPolicyOverride", {
    description: "Excepcion temporal a un parametro de la planta (ej: 'esta semana antelacion de combi 24 h por parada de planta'). Rige en el armado, el despacho, la asignacion y el cierre de las fechas de su rango.",
});
const overrideFields = {
    reason: z.string().trim().min(1).openapi({
        description: "Motivo obligatorio: por que se pisa temporalmente el parametro.",
    }),
    effectiveFrom: CalendarDate.openapi({
        description: "Primera fecha operativa (zona de la planta). No anterior a hoy en la planta (si no, 400).",
    }),
    effectiveTo: CalendarDate.openapi({
        description: "Ultima fecha operativa, inclusiva. No anterior a effectiveFrom ni a hoy; a lo sumo 31 dias de rango (si no, 400).",
    }),
};
// Un schema con nombre por parametro: el valor se valida con el mismo campo
// de UpdateSiteParametersRequest, asi los rangos son uno solo.
const createVariant = (field) => z
    .object({
    field: z.literal(field),
    value: UpdateSiteParametersRequest.shape[field].unwrap(),
    ...overrideFields,
})
    .strict()
    .openapi(`AdminCreateOverride${field.charAt(0).toUpperCase()}${field.slice(1)}`);
const updateVariant = (field) => z
    .object({
    field: z.literal(field).openapi({ description: "El parametro de la excepcion: no se cambia (si difiere, 400)." }),
    value: UpdateSiteParametersRequest.shape[field].unwrap().optional(),
    reason: overrideFields.reason.optional(),
    effectiveFrom: overrideFields.effectiveFrom.optional(),
    effectiveTo: overrideFields.effectiveTo.optional(),
})
    .strict()
    .openapi(`AdminUpdateOverride${field.charAt(0).toUpperCase()}${field.slice(1)}`);
export const AdminCreateOverrideMaxJourneyMin = createVariant("maxJourneyMin");
export const AdminCreateOverrideShuttleRequestLeadTimeHours = createVariant("shuttleRequestLeadTimeHours");
export const AdminCreateOverrideCabRequestLeadTimeHours = createVariant("cabRequestLeadTimeHours");
export const AdminCreateOverrideArrivalBufferMin = createVariant("arrivalBufferMin");
export const AdminCreateOverrideHandoverMarginMin = createVariant("handoverMarginMin");
export const AdminCreateOverrideCabAcceptDeadlineSeconds = createVariant("cabAcceptDeadlineSeconds");
export const AdminCreateOverrideCabAssignDeadlineSeconds = createVariant("cabAssignDeadlineSeconds");
export const AdminCreateOverrideShuttleAcceptDeadlineSeconds = createVariant("shuttleAcceptDeadlineSeconds");
export const AdminCreateOverrideShuttleAssignDeadlineSeconds = createVariant("shuttleAssignDeadlineSeconds");
export const AdminUpdateOverrideMaxJourneyMin = updateVariant("maxJourneyMin");
export const AdminUpdateOverrideShuttleRequestLeadTimeHours = updateVariant("shuttleRequestLeadTimeHours");
export const AdminUpdateOverrideCabRequestLeadTimeHours = updateVariant("cabRequestLeadTimeHours");
export const AdminUpdateOverrideArrivalBufferMin = updateVariant("arrivalBufferMin");
export const AdminUpdateOverrideHandoverMarginMin = updateVariant("handoverMarginMin");
export const AdminUpdateOverrideCabAcceptDeadlineSeconds = updateVariant("cabAcceptDeadlineSeconds");
export const AdminUpdateOverrideCabAssignDeadlineSeconds = updateVariant("cabAssignDeadlineSeconds");
export const AdminUpdateOverrideShuttleAcceptDeadlineSeconds = updateVariant("shuttleAcceptDeadlineSeconds");
export const AdminUpdateOverrideShuttleAssignDeadlineSeconds = updateVariant("shuttleAssignDeadlineSeconds");
export const AdminCreateOverrideRequest = z
    .discriminatedUnion("field", [
    AdminCreateOverrideMaxJourneyMin,
    AdminCreateOverrideShuttleRequestLeadTimeHours,
    AdminCreateOverrideCabRequestLeadTimeHours,
    AdminCreateOverrideArrivalBufferMin,
    AdminCreateOverrideHandoverMarginMin,
    AdminCreateOverrideCabAcceptDeadlineSeconds,
    AdminCreateOverrideCabAssignDeadlineSeconds,
    AdminCreateOverrideShuttleAcceptDeadlineSeconds,
    AdminCreateOverrideShuttleAssignDeadlineSeconds,
])
    .openapi("AdminCreateOverrideRequest", {
    description: "Alta de una excepcion temporal (0.7.4: fechas operativas en vez de vencimiento). Valor en el rango del parametro; motivo y rango obligatorios. Otra excepcion del mismo parametro con dias en comun: 409. Queda auditada.",
});
export const AdminUpdateOverrideRequest = z
    .discriminatedUnion("field", [
    AdminUpdateOverrideMaxJourneyMin,
    AdminUpdateOverrideShuttleRequestLeadTimeHours,
    AdminUpdateOverrideCabRequestLeadTimeHours,
    AdminUpdateOverrideArrivalBufferMin,
    AdminUpdateOverrideHandoverMarginMin,
    AdminUpdateOverrideCabAcceptDeadlineSeconds,
    AdminUpdateOverrideCabAssignDeadlineSeconds,
    AdminUpdateOverrideShuttleAcceptDeadlineSeconds,
    AdminUpdateOverrideShuttleAssignDeadlineSeconds,
])
    .openapi("AdminUpdateOverrideRequest", {
    description: "Desde 0.7.4. Edicion parcial de una excepcion vigente o programada: valor, motivo y rango (al menos uno, si no 400). Un inicio ya alcanzado no se mueve; dada de baja o vencida: 409. Queda auditada con antes y despues.",
});
export const AdminDeactivateOverrideRequest = z
    .object({
    reason: z.string().min(1).openapi({
        description: "Por que se levanta el override antes de su vencimiento.",
    }),
})
    .openapi("AdminDeactivateOverrideRequest", {
    description: "Baja anticipada (logica) de un override de politica: desde ese momento no rige en ninguna fecha.",
});
// --- Ficha completa (base + datos vivos del backoffice) ----------------------
export const AdminTenantFullDetail = AdminTenantDetail.extend({
    contacts: AdminContact.array()
        .optional()
        .openapi({ description: "Contactos del tenant (incluye dados de baja, con su marca)." }),
    agreements: AdminAgreements.optional().openapi({
        description: "Acuerdos contractuales sin dinero, si ya se cargaron.",
    }),
    overrides: AdminPolicyOverride.array()
        .optional()
        .openapi({ description: "Excepciones temporales sin baja que rigen hoy o van a regir (las vencidas y dadas de baja quedan en la auditoria)." }),
}).openapi("AdminTenantFullDetail", {
    description: "Lo que responde GET /admin/tenants/:siteId: la ficha base mas los datos vivos del backoffice. Los vivos son opcionales: un tenant recien onboardeado puede no tenerlos todavia.",
});
export const AdminIncidentsResponse = AdminIncident.array().openapi("AdminIncidentsResponse", {
    description: "GET /admin/tenants/:siteId/incidents: incidentes del tenant, recientes primero.",
});
// --- PATCH institucional del tenant ---------------------------------------------
// SOLO estos fields (razon social, cuit, grupo, notas y fechas de ciclo de
// vida), nada operativo. strict: cualquier field extra se rechaza con 400. La
// regla "al menos un campo" la aplica la ruta (no es un invariante del shape).
export const AdminUpdateTenantRequest = z
    .object({
    legalName: z.string().min(1).optional(),
    cuit: z.string().min(1).optional(),
    corporateGroup: z.string().min(1).optional(),
    internalNotes: z.string().optional(),
    firstContactAt: CalendarDate.optional(),
    pilotStartAt: CalendarDate.optional(),
    goLiveAt: CalendarDate.optional(),
})
    .strict()
    .openapi("AdminUpdateTenantRequest", {
    description: "PATCH institucional del tenant: solo identidad fiscal, grupo, notas y fechas de ciclo de vida. Campos fuera de esta lista se rechazan; hace falta al menos uno. Queda auditado con before y after.",
});
// --- Auditoria ------------------------------------------------------------------
export const AuditQuery = z
    .object({
    cursor: z.string().min(1).optional().openapi({
        description: "Cursor opaco devuelto por la pagina anterior (nextCursor).",
    }),
    limit: Integer.min(1).max(200).optional().openapi({
        description: "Tamano de pagina (1 a 200; por defecto 50).",
    }),
})
    .openapi("AuditQuery", { description: "Paginacion por cursor del log de auditoria." });
/** Valores textuales para conservar evidencia sin acoplarla a la entidad vigente. */
export const AuditStringField = z.object({
    name: z.string(),
    type: z.literal("string"),
    value: z.string(),
}).strict().openapi("AuditStringField");
export const AuditNumberField = z.object({
    name: z.string(),
    type: z.literal("number"),
    value: z.string().regex(/^-?(?:0|[1-9]\d*)(?:\.\d+)?(?:[eE][+-]?\d+)?$/),
}).strict().openapi("AuditNumberField");
export const AuditBooleanField = z.object({
    name: z.string(),
    type: z.literal("boolean"),
    value: z.enum(["true", "false"]),
}).strict().openapi("AuditBooleanField");
export const AuditNullField = z.object({
    name: z.string(),
    type: z.literal("null"),
}).strict().openapi("AuditNullField");
function isJsonContainer(value, array) {
    try {
        const parsed = JSON.parse(value);
        return parsed !== null && typeof parsed === "object" && Array.isArray(parsed) === array;
    }
    catch {
        return false;
    }
}
export const AuditObjectField = z.object({
    name: z.string(),
    type: z.literal("object"),
    value: z.string().regex(/^\{[\s\S]*\}$/)
        .refine((value) => isJsonContainer(value, false), "Objeto JSON invalido"),
}).strict().openapi("AuditObjectField");
export const AuditArrayField = z.object({
    name: z.string(),
    type: z.literal("array"),
    value: z.string().regex(/^\[[\s\S]*\]$/)
        .refine((value) => isJsonContainer(value, true), "Array JSON invalido"),
}).strict().openapi("AuditArrayField");
export const AuditField = z.discriminatedUnion("type", [
    AuditStringField, AuditNumberField, AuditBooleanField,
    AuditNullField, AuditObjectField, AuditArrayField,
]).openapi("AuditField");
// Desde 0.6.29 (PB-23): por que fallo un intento auditado. Catalogo cerrado:
// nunca el mensaje libre de un proveedor externo (Cognito, correo), que puede
// traer datos personales. Solo acompana a un evento con result "error".
export const AuditErrorCode = z.enum([
    "identity_provider_unavailable",
    "identity_create_failed",
    "identity_not_invitable",
    "target_not_found",
    "target_inactive",
    "activation_token_failed",
    "mail_delivery_failed",
    "persistence_failed",
    // PB-2a (0.7.2): el onboarding de una planta (apply_tenant_manifest) que no se aplico.
    "manifest_invalid",
    "manifest_outdated",
    "unexpected",
]).openapi("AuditErrorCode", {
    description: "Motivo de catalogo de un intento fallido. Nunca texto libre.",
});
export const AuditSnapshot = z.object({
    format: z.literal("fields_v1"),
    fields: z.array(AuditField),
    errorCode: AuditErrorCode.optional().openapi({
        description: "Motivo del fallo, solo en el snapshot posterior de un evento con result error. Ausente en eventos anteriores a 0.6.29.",
    }),
}).strict().openapi("AuditSnapshot");
export const AuditAction = z.enum([
    "create_web_account", "invite_web_account", "deactivate_web_account",
    "create_contact", "update_contact", "deactivate_contact", "update_agreements",
    "create_incident", "update_incident", "create_override", "update_override", "deactivate_override",
    "change_tenant_status", "update_tenant", "create_driver", "update_driver",
    "deactivate_driver", "create_document", "update_document", "delete_document",
    "cancel_ride_request", "provision_mobile_identity", "deactivate_mobile_identity", "invite_mobile_identity",
    "link_mobile_membership", "unlink_mobile_membership", "read_mobile_driver_trip",
    "create_provider_organization", "create_provider_subcontract", "update_provider_subcontract",
    "merge_provider_organization", "merge_driver_person", "merge_provider_vehicle",
    "create_vehicle", "update_vehicle", "deactivate_vehicle",
    "reinstate_driver", "reinstate_vehicle",
    "change_driver_site_status", "change_vehicle_site_status", "change_provider_site_status",
    "update_site_parameters", "update_provider_queue", "update_record_cab_sharing",
    "create_ride_request", "update_record_mobility_policy", "dispatch_ride_request",
    "open_document_alert", "acknowledge_document_alert", "resolve_document_alert",
    // ADR-022: importacion de planilla (conteos por motivo) y renovacion de domicilios geocodificados.
    "import_ride_request_spreadsheet", "refresh_geocoded_addresses",
    // PP-61 y PP-69 (0.6.41): lectura de nombres y domicilios de la hoja de ruta del proveedor.
    "read_provider_trip_passengers",
    // PP-68 (0.6.44): el operador prende o apaga el aviso de oferta nueva por mail.
    "update_provider_notification_preferences",
    // LQ-1 (0.7.0, ADR-026): terminos de liquidacion, correcciones de hechos y descarga del exportable (con conteos, sin montos).
    "update_settlement_terms", "correct_billable_facts", "download_trip_export",
    // PP-72 (0.7.1): lectura y edicion del domicilio escrito del legajo desde la consola, sin el texto.
    "read_record_home_address", "update_record_home_address",
    // PB-2a (0.7.2): cada ejecucion del onboarding de una planta, con el ARN IAM verificado y solo conteos.
    "apply_tenant_manifest",
    // MP-21 (0.7.3): el pasajero califico un viaje; sin estrellas ni chips.
    "rate_trip",
    // LQ-2 (0.7.5, ADR-026 §2.4): tarifarios desde la consola, con antes y despues completos.
    "create_rate_card", "create_rate_card_version", "update_rate_card_version", "publish_rate_card_version", "delete_rate_card_version",
    // LQ-3 (0.7.6, ADR-026 §5.1 y §6.1): recalculo guardado del periodo desde la consola, y lecturas y descargas
    // de la liquidacion con montos fuera de la consola (conteos, nunca montos en la fila).
    "recalculate_settlement_period", "read_settlement_draft", "download_settlement_export",
    // LQ-6 (0.7.7, ADR-026 §5): transiciones del periodo desde la consola, conformidad y observaciones de cada parte
    // (actor del token; el comentario nunca va a la fila) y notas de ajuste ordenadas (conteos, sin montos).
    "start_settlement_review", "close_settlement_period", "approve_settlement_period",
    "open_settlement_dispute", "resolve_settlement_dispute", "create_settlement_adjustments",
    "legacy_unknown",
]).openapi("AuditAction");
export const AuditResult = z.enum(["ok", "error", "legacy_unknown"]).openapi("AuditResult");
// Desde 0.6.17: el actor de una cuenta mobile resuelto por la API, para que
// la consola no muestre el sub crudo. Solo rol y legajo: nunca nombre ni email.
export const AuditMobileActor = z.object({
    kind: z.literal("mobile_account"),
    role: MobileApplication,
    record: RecordId.optional().openapi({
        description: "Legajo del pasajero en la planta del evento (targetTenant). Ausente para conductores, sin planta o sin membresia en esa planta.",
    }),
}).strict().openapi("AuditMobileActor", {
    description: "Cuenta mobile que actuo: rol y, para pasajeros, legajo en la planta del evento.",
});
export const AuditActor = z.discriminatedUnion("kind", [AuditMobileActor]).openapi("AuditActor", {
    description: "Actor resuelto por la API cuando `admin` es un identificador opaco. Ausente si no se pudo resolver o si `admin` ya es legible.",
});
export const AdminAuditEvent = z
    .object({
    id: Integer.positive().openapi({
        description: "Identificador secuencial del evento; tambien sirve de cursor de paginacion.",
    }),
    instant: Instant,
    admin: z.string().min(1).openapi({
        description: "Identificador del actor verificado: identidad Cognito o ARN IAM para operaciones administradas.",
    }),
    actor: AuditActor.optional(),
    action: AuditAction,
    legacyAction: z.string().optional().openapi({ description: "Accion original cuando no pertenece al catalogo historico conocido." }),
    resource: z.string().min(1).openapi({
        description: "Identificador del recurso afectado.",
        example: "contacts/site-bahia-blanca/c-01",
    }),
    targetTenant: SiteId.optional().openapi({
        description: "Tenant sobre el que se actuo; ausente en acciones globales de plataforma.",
    }),
    before: AuditSnapshot.optional().openapi({ description: "Snapshot anterior; ausente cuando no se registro. Null de un campo no implica ausencia." }),
    after: AuditSnapshot.optional().openapi({ description: "Snapshot posterior; ausente cuando no se registro." }),
    result: AuditResult,
    legacyResult: z.string().optional().openapi({ description: "Resultado original no reconocido, sin inferir exito ni error." }),
})
    .openapi("AdminAuditEvent", {
    description: "Un evento del log de auditoria administrativa: quien, cuando, que recurso, valor anterior y nuevo. El log es append-only en base.",
});
export const AdminAuditResponse = z
    .object({
    events: z.array(AdminAuditEvent).openapi({
        description: "Pagina de eventos, del mas reciente al mas viejo.",
    }),
    nextCursor: z.string().min(1).optional().openapi({
        description: "Cursor opaco para pedir la pagina siguiente (eventos mas viejos). Ausente cuando no hay mas paginas.",
    }),
})
    .openapi("AdminAuditResponse", {
    description: "Log de auditoria paginado por cursor. La vista global lo muestra completo; la ficha de tenant lo pre-filtra a ese tenant.",
});
// --- Salud diaria ---------------------------------------------------------------
export const AdminDailyHealth = z
    .object({
    date: CalendarDate,
    freshMinutes: Integer.min(0).openapi({
        description: "Minutos del dia con datos frescos (el tick vio posiciones a tiempo).",
    }),
    degradedMinutes: Integer.min(0).openapi({
        description: "Minutos del dia con servicio degradado (tick atrasado con viajes en curso).",
    }),
})
    .openapi("AdminDailyHealth", {
    description: "Salud del servicio de un tenant en un dia: la celda de la barra de uptime de 90 dias.",
});
export const AdminTenantHealthResponse = z
    .object({
    siteId: SiteId,
    days: z.array(AdminDailyHealth).max(90).openapi({
        description: "Hasta 90 dias de salud diaria, del mas viejo al mas reciente. Los dias sin fila son dias sin datos (anteriores al alta o sin operacion).",
    }),
})
    .openapi("AdminTenantHealthResponse", {
    description: "Serie de salud diaria de un tenant para la UptimeBar de 90 dias estilo Statuspage.",
});
// --- Ciclo de vida del tenant -----------------------------------------------------
export const AdminTenantStatusChangeRequest = z
    .object({
    status: SiteStatus.openapi({
        description: "Estado nuevo del ciclo de vida comercial del tenant.",
    }),
    reason: z.string().min(1).openapi({
        description: "Por que cambia el estado. Obligatorio: suspender o dar de baja un tenant es una accion de danger zona.",
    }),
})
    .openapi("AdminTenantStatusChangeRequest", {
    description: "Cambio de estado del ciclo de vida de un tenant (ej: activa -> suspendida). En la UI exige type-to-confirm del slug; en la API queda auditado con before y after.",
});
//# sourceMappingURL=admin.js.map